import { useAlarms, useAlarmsMedia } from '@hakimo-ui/hakimo/data-access';
import { AlarmGroupContainer } from '@hakimo-ui/hakimo/feature-shared';
import {
  AlarmMediaResponse,
  AlarmsDTO,
  DataVersion,
  LocationAlarm,
  MotionAlarm,
  SOPViewType,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  transformLocationAlarmToUnifiedAlarmGroup,
  transformRawAlarmToUnifiedAlarm,
  withAuthz,
} from '@hakimo-ui/hakimo/util';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMinorAlarmsSearchParams } from './util';

interface Props {
  locationAlarm: LocationAlarm;
  onAlarmResolve?: () => void;
  dataVersion?: DataVersion;
}
export function LocationAlarmInvestigateView(props: Props) {
  const { locationAlarm, onAlarmResolve, dataVersion } = props;
  const [rawAlarmToMediaMap, setRawAlarmToMediaMap] = useState<
    Map<string, AlarmMediaResponse>
  >(new Map());
  const [unifiedAlarmGroup, setUnifiedAlarmGroup] = useState<UnifiedAlarmGroup>(
    transformLocationAlarmToUnifiedAlarmGroup(locationAlarm)
  );
  const { alarmTime, id: locationAlarmId } = locationAlarm;

  const { mutate: loadMedia } = useAlarmsMedia((data) => {
    setRawAlarmToMediaMap((prev) => {
      const updatedMap = new Map(prev);
      data.forEach((media) => {
        updatedMap.set(media.alarmId, media);
      });
      return updatedMap;
    });
  }, dataVersion);

  const queryParamsForMinorAlarms = useMemo(
    () => getMinorAlarmsSearchParams(locationAlarm),
    [locationAlarm]
  );

  const onMinorAlarmsSuccess = useCallback(
    (data: AlarmsDTO) => {
      const transformedRawAlarms = data.items.map((alarm) =>
        transformRawAlarmToUnifiedAlarm(locationAlarmId, alarm as MotionAlarm)
      );
      if (unifiedAlarmGroup && transformedRawAlarms.length > 0) {
        // Merge by id: this runs on every successful fetch of the minor-alarms
        // query, so a plain append duplicated every alarm on each refetch.
        const existingIds = new Set(unifiedAlarmGroup.alarms.map((a) => a.id));
        const newRawAlarms = transformedRawAlarms.filter(
          (a) => !existingIds.has(a.id)
        );
        if (newRawAlarms.length === 0) {
          return;
        }
        setUnifiedAlarmGroup({
          ...unifiedAlarmGroup,
          alarms: [...unifiedAlarmGroup.alarms, ...newRawAlarms],
        });
        const videoAlarmIds = newRawAlarms
          .filter((a) => a.media.type !== 'frames')
          .map((a) => a.id);

        if (videoAlarmIds.length > 0) {
          loadMedia({
            alarmIds: videoAlarmIds,
            includeBoxes: true,
            timestamp: alarmTime,
            locationAlarmId,
          });
        }
      }
    },
    [unifiedAlarmGroup, alarmTime, locationAlarmId, loadMedia]
  );

  useAlarms(
    queryParamsForMinorAlarms,
    undefined,
    onMinorAlarmsSuccess,
    false,
    false,
    dataVersion
  );

  useEffect(() => {
    // Only fetch video media for alarms that don't already carry embedded frame
    // data from the location-alarm API response.
    const videoAlarmIds = locationAlarm.rawAlarms
      .filter((a) => !a.media?.frames?.length)
      .map((a) => a.id);
    if (videoAlarmIds.length > 0) {
      loadMedia({
        alarmIds: videoAlarmIds,
        includeBoxes: true,
        timestamp: alarmTime,
        locationAlarmId,
      });
    }
  }, [locationAlarm.rawAlarms, alarmTime, locationAlarmId, loadMedia]);

  useEffect(() => {
    // attach media to raw alarms
    if (rawAlarmToMediaMap.size > 0) {
      const updatedAlarmGroup = { ...unifiedAlarmGroup };
      const rawAlarms = updatedAlarmGroup.alarms.map((alarm) => {
        const alarmMedia = rawAlarmToMediaMap.get(alarm.id);
        if (alarm.media.video) {
          alarm.media.video.url = alarmMedia?.url || '';
          alarm.media.video.boxes = alarmMedia?.boxes || [];
        }
        return alarm;
      });
      updatedAlarmGroup.alarms = rawAlarms;
      setUnifiedAlarmGroup(updatedAlarmGroup);
    }
  }, [rawAlarmToMediaMap, locationAlarm]);

  // The group carries its own product (event-flow marks enterprise groups).
  // Forcing REMOTE_GUARDING here made an enterprise alarm render as RG in this
  // view while rendering as enterprise in Unified Monitoring.
  const product =
    unifiedAlarmGroup?.product ?? UnifiedMonitoringProduct.REMOTE_GUARDING;

  return (
    <div className="relative h-full">
      <AlarmGroupContainer
        alarmGroup={unifiedAlarmGroup}
        product={product}
        onResolveAlarm={onAlarmResolve}
        sopViewType={SOPViewType.STATIC}
        dataVersion={dataVersion}
      />
    </div>
  );
}

export default withAuthz(LocationAlarmInvestigateView, [
  'location_alarm/detail:view',
]);
